import React, { useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { RateMultiplier } from '@/types';
import type { ExtendedTier } from '@/types/ExtendedTier';
import { fieldLabelClass, fieldDescriptionClass } from './surcharge/fieldStyles';
import { NumberStepper } from '@/components/ui/number-stepper';
import { FixedMultiplierField } from './surcharge/FixedMultiplierField';
import { CustomizedFieldIndicator } from '@/components/form/CustomizedFieldIndicator';
import { ExtendedOvertimeSection } from './ExtendedOvertimeSection';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';

export type OffsetDomain = 'short_turn' | 'holiday' | 'meal_break' | 'overnight';
export type OffsetMethod =
  | 'offset_overtime'
  | 'carry_over'
  | 'bump_up'
  | 'guaranteed_rate';

export interface OffsetConfig {
  floor_source: 'none' | 'fixed_tier' | 'previous_shift';
  overtime_offset: number;
  floor_tier_code: string | null;
  floor_tier_custom_value: number | null;
}

export interface OffsetConfigSectionProps {
  domain: OffsetDomain;
  method: OffsetMethod;
  config: OffsetConfig;
  rateMultipliers: RateMultiplier[];
  /** Optional override for the tier <select> source. Defaults to rateMultipliers. */
  tierPickerMultipliers?: RateMultiplier[];
  /** Optional filter applied to the picker list (after tierPickerMultipliers resolves). Does NOT affect bump-method empty-state logic. */
  tierPickerMultiplierFilter?: (m: RateMultiplier) => boolean;
  /** Optional error element to render below the tier picker. When provided, supersedes the synthesized "Unknown tier" message. */
  floorTierErrorElement?: React.ReactNode;
  extendedOvertimeEnabled?: boolean;
  extendedOvertimeTiers?: ExtendedTier[];
  /** Grouped prop for composing <ExtendedOvertimeSection>. When provided on a ladder-aware method
   *  (offset_overtime, bump_up, guaranteed_rate), mounts the editor. Takes precedence over the
   *  flat extendedOvertimeEnabled/extendedOvertimeTiers props for preview resolution. */
  extendedOvertime?: {
    enabled: boolean;
    onEnabledChange: (enabled: boolean) => void;
    tiers: ExtendedTier[];
    onTiersChange: (tiers: ExtendedTier[]) => void;
    highestOvertimeRate: number;
    highestOvertimeTierName?: string;
    defaultTiers?: ExtendedTier[];
    maxTiers?: number;
    isCustomized?: boolean;
    configureRef?: React.MutableRefObject<(() => void) | null>;
    helpContent?: string;
    helpFieldNamePrefix?: string;
    helpVariables?: Record<string, unknown>;
    helpInjectableComponents?: Record<string, React.ReactNode>;
  };
  context?: 'user' | 'client';
  customizedFields?: {
    floorTier?: boolean;
    overtimeOffset?: boolean;
    floorSource?: boolean;
  };
  onChange: (next: OffsetConfig) => void;
}

// Stub primitives — Phase 9 integration replaces these with real ConfigSection / ControlRow wrappers.
// Styling follows "Road Case" Pro-Industrial: surface-2 inputs, amber focus, 0.25rem radius, Plex Mono numbers.

// `text-base sm:text-sm` keeps iOS from zooming on focus (<16px triggers zoom)
// while restoring the denser desktop scale. `min-h-[44px]` guarantees touch target.
const inputClass =
  'bg-surface-2 border border-border rounded-[0.25rem] px-3 py-2 min-h-[44px] ' +
  'font-mono text-base sm:text-sm text-foreground shadow-inner ' +
  'focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const errorClass =
  'mt-1 text-[12px] font-label uppercase tracking-wide text-destructive';

const sectionClass = 'flex flex-col gap-3';

const FLOOR_SOURCE_ORDER = ['none', 'fixed_tier', 'previous_shift'] as const;
type FloorSourceLiteral = (typeof FLOOR_SOURCE_ORDER)[number];

export function OffsetConfigSection(props: OffsetConfigSectionProps) {
  const { domain, method, config, rateMultipliers, onChange } = props;

  // Roving tabindex refs for floor-source radiogroup
  const floorSourceButtonRefs = useRef<
    Record<FloorSourceLiteral, HTMLButtonElement | null>
  >({ none: null, fixed_tier: null, previous_shift: null });

  const pickerMultipliers = props.tierPickerMultipliers ?? rateMultipliers;

  const filteredPickerMultipliers = props.tierPickerMultiplierFilter
    ? pickerMultipliers.filter(props.tierPickerMultiplierFilter)
    : pickerMultipliers;

  const bumpMethodsEmpty = rateMultipliers.length === 0;
  // Banner fires for guaranteed_rate only when the picker SOURCE itself is empty.
  const pickerSourceEmpty = pickerMultipliers.length === 0;
  // Inline helper fires when source has items but the filter strips them all.
  const filterLeavesZero = !pickerSourceEmpty && filteredPickerMultipliers.length === 0;

  const showEmptyBanner =
    (method === 'offset_overtime' && bumpMethodsEmpty) ||
    (method === 'bump_up' && bumpMethodsEmpty) ||
    (method === 'guaranteed_rate' && pickerSourceEmpty);

  if (showEmptyBanner) {
    return (
      <div
        className="offset-config-disabled bg-surface-1 border border-border rounded-[0.25rem] p-4 text-sm text-muted-foreground flex items-start gap-2"
        data-testid="offset-config-empty"
        role="status"
      >
        <AlertTriangle
          aria-hidden
          className="h-4 w-4 mt-0.5 text-primary shrink-0"
        />
        <span>
          Configure overtime multipliers before selecting this method.{' '}
          <Link
            to="/profile/contract"
            className="text-primary underline underline-offset-2 hover:text-primary/80"
          >
            Go to multiplier settings →
          </Link>
        </span>

      </div>
    );
  }

  const showBumpStepper = method === 'offset_overtime' || method === 'bump_up';
  const showTierPicker = method === 'guaranteed_rate';
  const showFloorSourcePicker = domain === 'short_turn' && method === 'guaranteed_rate';
  const maxBump = Math.min(10, Math.max(1, rateMultipliers.length - 1));
  const bumpDisabled = rateMultipliers.length <= 1;
  const bumpOutOfRange = !bumpDisabled && (config.overtime_offset < 1 || config.overtime_offset > maxBump);

  const tierCodeKnown =
    !config.floor_tier_code ||
    filteredPickerMultipliers.some((m) => m.code === config.floor_tier_code);

  const handleFloorSourceKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    currentIdx: number,
  ) => {
    let nextIdx: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIdx = (currentIdx + 1) % FLOOR_SOURCE_ORDER.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIdx =
        (currentIdx - 1 + FLOOR_SOURCE_ORDER.length) % FLOOR_SOURCE_ORDER.length;
    } else if (e.key === 'Home') {
      nextIdx = 0;
    } else if (e.key === 'End') {
      nextIdx = FLOOR_SOURCE_ORDER.length - 1;
    }
    if (nextIdx !== null) {
      e.preventDefault();
      const nextSrc = FLOOR_SOURCE_ORDER[nextIdx];
      onChange({ ...config, floor_source: nextSrc });
      floorSourceButtonRefs.current[nextSrc]?.focus();
    }
  };

  return (
    <div
      className={`offset-config-section ${sectionClass}`}
      data-domain={domain}
      data-method={method}
    >
      {showBumpStepper && (
        <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
          <div className="min-w-0 space-y-1">
            <label className={`${fieldLabelClass} inline-flex items-center gap-1.5`} htmlFor="offset-overtime-offset-stepper">
              <span>Overtime Offset</span>
              <CustomizedFieldIndicator
                isCustomized={props.customizedFields?.overtimeOffset}
                context={props.context}
              />
            </label>
            <p className={`${fieldDescriptionClass} mt-1 hidden md:block`}>
              How many tiers up the overtime rate ladder to bump the surcharge.
            </p>
          </div>
          <div className="flex flex-col items-end md:w-full">
            <NumberStepper
              id="offset-overtime-offset-stepper"
              label="Overtime Offset"
              value={config.overtime_offset}
              min={1}
              max={maxBump}
              disabled={bumpDisabled}
              ariaInvalid={bumpOutOfRange || undefined}
              ariaDescribedby={bumpOutOfRange ? 'offset-overtime-offset-error' : undefined}
              testId="overtime-offset-stepper"
              decrementTestId="overtime-offset-decrement"
              incrementTestId="overtime-offset-increment"
              onChange={(next) => onChange({ ...config, overtime_offset: next })}
            />
            {bumpDisabled && (
              <p className={`${fieldDescriptionClass} text-right mt-1`}>
                Add overtime multipliers to configure tier offsets.
              </p>
            )}
            {bumpOutOfRange && (
              <div
                id="offset-overtime-offset-error"
                data-testid="overtime-offset-error"
                className={errorClass}
                role="alert"
              >
                Overtime Offset must be between 1 and {maxBump}.
              </div>
            )}
          </div>
          <p className={`${fieldDescriptionClass} col-span-2 md:hidden`}>
            How many tiers up the overtime rate ladder to bump the surcharge.
          </p>
        </div>
      )}

      {showFloorSourcePicker && (
        <div className="flex flex-col gap-1" data-testid="floor-source-picker">
          <label className={`${fieldLabelClass} inline-flex items-center gap-2`} id="floor-source-label">
            <span>Floor source</span>
            <CustomizedFieldIndicator
              isCustomized={props.customizedFields?.floorSource}
              context={props.context}
            />
          </label>
          <div
            role="radiogroup"
            aria-labelledby="floor-source-label"
            aria-label="Floor source"
            className="flex gap-2"
          >
            {FLOOR_SOURCE_ORDER.map((src, idx) => {
              const checked = config.floor_source === src;
              return (
                <button
                  key={src}
                  ref={(el) => {
                    floorSourceButtonRefs.current[src] = el;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked ? 0 : -1}
                  onKeyDown={(e) => handleFloorSourceKeyDown(e, idx)}
                  className={
                    'min-h-[44px] px-3 py-2 text-base sm:text-sm font-label uppercase tracking-wide rounded-[0.25rem] border ' +
                    (checked
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-surface-2 border-border text-foreground')
                  }
                  onClick={() => onChange({ ...config, floor_source: src })}
                >
                  {src.replace('_', ' ')}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showTierPicker && (
        filterLeavesZero ? (
          <div role="status" className="text-sm text-muted-foreground">
            Add non-straight-time multipliers to pick a floor tier.
          </div>
        ) : (
          <FixedMultiplierField
            isFloor
            rateMultipliers={filteredPickerMultipliers}
            selectedCode={config.floor_tier_code}
            onSelectionChange={(code) =>
              onChange({
                ...config,
                floor_tier_code: code,
                floor_tier_custom_value: code ? null : config.floor_tier_custom_value,
              })
            }
            customValue={config.floor_tier_custom_value}
            onCustomValueChange={(value) => {
              const clamped = value == null ? null : Math.min(9.99, Math.max(1.01, value));
              onChange({
                ...config,
                floor_tier_custom_value: clamped,
                floor_tier_code: clamped != null ? null : config.floor_tier_code,
              });
            }}
            customizedFieldKey={`${domain}_floor_tier_code`}
            isCustomized={props.customizedFields?.floorTier}
            context={props.context}
            errorElement={
              props.floorTierErrorElement ?? (
                !tierCodeKnown ? (
                  <div className={errorClass} role="alert">Unknown tier — please re-select.</div>
                ) : null
              )
            }
          />
        )
      )}

      {!!props.extendedOvertime &&
        (method === 'offset_overtime' || method === 'bump_up' || method === 'guaranteed_rate') && (
        <AnimatedCollapse open>
          <ExtendedOvertimeSection
            enabled={props.extendedOvertime.enabled}
            onEnabledChange={props.extendedOvertime.onEnabledChange}
            tiers={props.extendedOvertime.tiers}
            onTiersChange={props.extendedOvertime.onTiersChange}
            highestOvertimeRate={props.extendedOvertime.highestOvertimeRate}
            highestOvertimeTierName={props.extendedOvertime.highestOvertimeTierName}
            defaultTiers={props.extendedOvertime.defaultTiers}
            maxTiers={props.extendedOvertime.maxTiers}
            isCustomized={props.extendedOvertime.isCustomized}
            context={props.context ?? 'user'}
            configureRef={props.extendedOvertime.configureRef}
            helpContent={props.extendedOvertime.helpContent}
            helpFieldNamePrefix={props.extendedOvertime.helpFieldNamePrefix}
            helpVariables={props.extendedOvertime.helpVariables}
            helpInjectableComponents={props.extendedOvertime.helpInjectableComponents}
          />
        </AnimatedCollapse>
      )}
    </div>
  );
}
